import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { debug } from '@/utils/debug';
import { meaningfulStatus } from '@/utils/formatEnum';
import type { AppConfiguration } from '@/types/api';

/**
 * Per-campaign local UI state for Meta cards.
 *
 * Mirrors `campaignSettingsGoogleAdsStore`'s `CampaignBudgetSettings`
 * minus the multi-campaign budget pool concept — Meta doesn't have a
 * shared allocatable budget surface at A4.2 scope, so the
 * `getTotalCurrentBudget` / `setTotalBudget` plumbing is omitted.
 *
 * Name edits are tracked via `manualName` (raw pending value) +
 * `manualNameChanges`. Budget edits (Phase 1b) follow the same shape
 * via `manualBudget` + `manualBudgetChanges`. Unlike Google, the
 * actual save dispatch goes through `pendingChangesStore` with
 * `platform: 'meta_ads'` (one Meta batch round-trip for status +
 * rename + budget) — Google has dedicated per-property endpoints +
 * a `preSave` hook, Meta does not.
 *
 * Eligibility (`isCbo` + `isDailyBudget`) is seeded from the live
 * `MetaCampaign` payload so consumers can filter ABO / lifetime-budget
 * campaigns out of the simplex candidate pool without re-reading the
 * MetaCampaign object. Phase 1 supports CBO + daily only.
 */
export interface MetaCampaignSettings {
  campaignId: string;
  manualName: string | null;
  currentName: string;
  campaignStatus: string; // ACTIVE | PAUSED | ARCHIVED
  // Server baseline at last init / hydrate. Mirrors
  // `originalOptimizationEnabled`. Discard reverts `campaignStatus`
  // to this so a pause/enable that the user later discards doesn't
  // leave the store stuck on an optimistic value (which would then
  // exclude the campaign from `useOptimizedCampaigns` until the next
  // refetch). Tracked separately because the live API status is the
  // only source of truth — neither `manualName` / `manualBudget`
  // round-trip carry it.
  originalCampaignStatus: string;
  metaAdAccountId: string | null;
  nameValidationError: string | null;
  isOptimizationEnabled: boolean;
  originalOptimizationEnabled: boolean;
  // Budget surface (Phase 1b parity with Google):
  // `manualBudget` = staged user edit (major units, e.g. dollars).
  //   `null` when the user hasn't staged an edit OR for lifetime
  //   campaigns (`daily_budget IS NULL`) — the BudgetBar UI is gated
  //   off in that case so there's no path to a staged value.
  // `currentBudget` = server-baseline daily_budget at last init/hydrate.
  //   Typed `number` (not nullable) — seeded to `0` for lifetime
  //   campaigns. The optimization-pool filter is `isCbo &&
  //   isDailyBudget`, not a null check, so lifetime entries don't reach
  //   the simplex regardless of the 0 sentinel.
  manualBudget: number | null;
  currentBudget: number;
  // Eligibility predicates seeded from MetaCampaign.is_cbo + daily_budget
  // at init time. `isDailyBudget` true when daily_budget !== null. Both
  // must be true for the FlowerButton/BudgetBar surface to render
  // enabled — ABO + lifetime are deferred (Phase 2).
  isCbo: boolean;
  isDailyBudget: boolean;
}

interface MetaCampaignSettingsStore {
  campaignSettings: Map<string, MetaCampaignSettings>;
  manualNameChanges: Set<string>;
  manualBudgetChanges: Set<string>;
  optimizationToggleChanges: Set<string>;

  setCampaignSettings: (
    campaignId: string,
    settings: Partial<MetaCampaignSettings>,
  ) => void;
  setManualName: (campaignId: string, name: string) => void;
  /**
   * Stage a manual budget edit. Mirrors Google's
   * `setManualBudget` epsilon-compare so a toggled-then-untoggled edit
   * drops out of the dirty Set instead of leaving stale dirty state.
   */
  setManualBudget: (campaignId: string, budget: number) => void;
  /**
   * Toggle flouri_optimize_budget for a Meta campaign. Wired to the
   * MetaCampaignCard FlowerButton. Mirrors Google's diff-against-
   * `originalOptimizationEnabled` logic so a toggled-then-untoggled
   * change drops out of the dirty Set.
   */
  toggleCampaignOptimization: (campaignId: string, enabled: boolean) => void;
  updateCampaignStatus: (campaignId: string, status: string) => void;
  /**
   * Bulk-seed settings from the API response.
   *
   * For each incoming campaign:
   * - `currentName`, `campaignStatus`, `metaAdAccountId`,
   *   `isOptimizationEnabled`, `currentBudget`, `isCbo`, `isDailyBudget`
   *   are refreshed from the server payload — these reflect Meta-side
   *   truth and should follow it.
   * - `manualName` / `nameValidationError` are preserved when the
   *   campaign has an in-flight `manualNameChanges` membership.
   *   `manualBudget` is preserved under the same rule via
   *   `manualBudgetChanges`. Without the membership check, completed
   *   edits would survive forever and clobber server truth on every
   *   refetch.
   *
   * Not idempotent in the "every field preserved" sense — server-side
   * fields are refreshed by design; user-edited fields are preserved
   * only when actually in flight.
   */
  initializeCampaignSettings: (
    campaigns: Array<{
      campaign_id: string;
      campaign_name: string;
      campaign_status?: string | null;
      meta_ad_account_id?: string | null;
      flouri_optimize_budget?: boolean;
      daily_budget?: number | null;
      is_cbo?: boolean;
    }>,
  ) => void;
  /**
   * Remove a campaign's pending name edit after a successful save.
   */
  clearNameChange: (campaignId: string) => void;
  /**
   * Remove a campaign's pending budget edit after a successful save.
   * Parallel to `clearNameChange`; called by `pendingChangesStore`'s
   * Meta budget post-success cleanup.
   */
  clearBudgetChange: (campaignId: string) => void;
  /**
   * Discard all pending edits — wired to SaveChangesBar's "Discard" CTA
   * via the pendingChangesStore.executeDiscardCallbacks pipeline.
   */
  discardChanges: () => void;

  /**
   * Slice-provider surface for appConfigService. Mirrors the Google store.
   * Meta contributes ONLY existing entries (overlayed with in-memory
   * manualName / isOptimizationEnabled if staged); new Meta campaigns are
   * authored by the backend's regenerate_config_on_demand, not the
   * frontend, so toConfigSlice never invents entries from scratch.
   */
  toConfigSlice: (
    existing?: AppConfiguration['configuration'] | null,
  ) => Partial<AppConfiguration['configuration']>;
  hydrateFromConfig: (
    config: AppConfiguration['configuration'] | undefined | null,
  ) => void;
  markSaved: () => void;
}

/**
 * Validate a Meta campaign name per §8.4: 1–400 chars, no control chars.
 * Backend re-validates via `RenameMetaEntityRequest`'s Pydantic field
 * constraints — client-side check is for inline UX feedback only.
 *
 * Exported so card-level handlers (MetaCampaignCard.handleSaveName) can
 * gate the queue dispatch on the same rule the store uses, without
 * round-tripping through `nameValidationError` on the store state.
 */
export function validateMetaCampaignName(
  name: string,
): { isValid: boolean; sanitizedName: string; error: string | null } {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return { isValid: false, sanitizedName: trimmed, error: 'Name is required.' };
  }
  if (trimmed.length > 400) {
    return {
      isValid: false,
      sanitizedName: trimmed,
      error: 'Name must be 400 characters or fewer.',
    };
  }
  // Reject C0 + DEL (0x00–0x1F, 0x7F). Same regex as the backend
  // RenameMetaEntityRequest field validator.
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(trimmed)) {
    return {
      isValid: false,
      sanitizedName: trimmed,
      error: 'Name cannot contain control characters.',
    };
  }
  return { isValid: true, sanitizedName: trimmed, error: null };
}

export const useCampaignSettingsMetaAdsStore = create<MetaCampaignSettingsStore>()(
  devtools(
    (set, get) => ({
      campaignSettings: new Map(),
      manualNameChanges: new Set(),
      manualBudgetChanges: new Set(),
      optimizationToggleChanges: new Set(),

      setCampaignSettings: (campaignId, partial) => {
        set((state) => {
          const newMap = new Map(state.campaignSettings);
          const existing = newMap.get(campaignId);
          newMap.set(campaignId, {
            campaignId,
            manualName: null,
            currentName: '',
            campaignStatus: 'PAUSED',
            originalCampaignStatus: 'PAUSED',
            metaAdAccountId: null,
            nameValidationError: null,
            isOptimizationEnabled: false,
            originalOptimizationEnabled: false,
            manualBudget: null,
            currentBudget: 0,
            isCbo: false,
            isDailyBudget: false,
            ...existing,
            ...partial,
          });
          return { campaignSettings: newMap };
        });
      },

      setManualName: (campaignId, name) => {
        const validation = validateMetaCampaignName(name);
        const { campaignSettings, manualNameChanges, setCampaignSettings } = get();
        setCampaignSettings(campaignId, {
          manualName: validation.sanitizedName,
          nameValidationError: validation.error,
        });
        const updated = new Set(manualNameChanges);
        const settings = campaignSettings.get(campaignId);
        const originalName = (settings?.currentName || '').trim();
        if (validation.isValid && validation.sanitizedName !== originalName) {
          updated.add(campaignId);
        } else {
          updated.delete(campaignId);
        }
        set({ manualNameChanges: updated });
        if (!validation.isValid) {
          debug.log('Meta campaign name validation failed', {
            campaignId, name, error: validation.error,
          });
        }
      },

      setManualBudget: (campaignId, budget) => {
        const { setCampaignSettings, manualBudgetChanges, campaignSettings } = get();
        setCampaignSettings(campaignId, { manualBudget: budget });

        const settings = campaignSettings.get(campaignId);
        const originalBudget = settings?.currentBudget ?? 0;
        const updated = new Set(manualBudgetChanges);
        // Epsilon-compare (matches Google's tolerance at
        // campaignSettingsGoogleAdsStore.ts setManualBudget). A toggled-
        // then-untoggled edit drops out of the Set so SaveChangesBar
        // doesn't count it as dirty.
        if (Math.abs(budget - originalBudget) > 0.001) {
          updated.add(campaignId);
        } else {
          updated.delete(campaignId);
        }
        set({ manualBudgetChanges: updated });
      },

      toggleCampaignOptimization: (campaignId, enabled) => {
        const { setCampaignSettings, optimizationToggleChanges, campaignSettings } = get();
        setCampaignSettings(campaignId, { isOptimizationEnabled: enabled });

        const settings = campaignSettings.get(campaignId);
        const originalValue = settings?.originalOptimizationEnabled ?? false;
        const updated = new Set(optimizationToggleChanges);
        if (enabled !== originalValue) {
          updated.add(campaignId);
        } else {
          updated.delete(campaignId);
        }
        set({ optimizationToggleChanges: updated });
      },

      updateCampaignStatus: (campaignId, status) => {
        get().setCampaignSettings(campaignId, { campaignStatus: status });
      },

      initializeCampaignSettings: (campaigns) => {
        // Compute outside `set` and bail without calling it when
        // nothing changed. Returning `{}` from a `set((state) => ...)`
        // updater is NOT a no-op in Zustand — the default merge
        // produces a new state object via Object.assign, which still
        // bumps the global state reference and fires whole-store
        // subscribers. The idempotency guard only works if we skip
        // `set` entirely. Mirrors Google's "compute newMap, then
        // `if (differs) set({...})`" pattern in
        // `campaignSettingsGoogleAdsStore.initializeCampaignSettings`.
        const state = get();
        // Build the Map FROM SCRATCH off the incoming payload rather
        // than copy-then-merge into the existing Map. The merge form
        // never drops entries absent from the latest payload, so a
        // Meta account unlink (or any campaign removed server-side)
        // would leave stale settings in the store — those entries
        // then bleed into `useOptimizedCampaigns()` totals + the
        // optimizer candidate pool. Mirrors Google's rebuild posture.
        const newMap = new Map<string, MetaCampaignSettings>();
        // Size differing means at least one campaign was added or
        // removed — flip `changed` so the rebuild propagates even if
        // every overlapping campaign is value-equal.
        let changed = state.campaignSettings.size !== campaigns.length;
        for (const c of campaigns) {
          const existing = state.campaignSettings.get(c.campaign_id);
          // Preserve `manualName` + `nameValidationError` ONLY when
          // the campaign actually has an in-flight unsaved edit
          // (tracked by `manualNameChanges`). Without the membership
          // check, a manual edit that the user finished + saved would
          // stick around on the entry forever — every subsequent
          // refetch would re-copy the stale value into the new entry
          // and the UI would show the old typed value instead of
          // server truth. Same shape for `manualBudget` via
          // `manualBudgetChanges` (Phase 1b).
          const hasNameEdit = state.manualNameChanges.has(c.campaign_id);
          const hasBudgetEdit = state.manualBudgetChanges.has(c.campaign_id);
          const hasToggle = state.optimizationToggleChanges.has(c.campaign_id);
          const optimizationEnabled = Boolean(c.flouri_optimize_budget);
          const dailyBudget = c.daily_budget ?? null;
          const serverStatus = c.campaign_status || 'PAUSED';
          const next: MetaCampaignSettings = {
            campaignId: c.campaign_id,
            currentName: c.campaign_name,
            // Preserve in-flight optimistic status from a pause/enable
            // that hasn't been saved or discarded yet — mirrors the
            // `hasToggle` pattern below. Without this, a refetch
            // (which fires this init) would clobber the optimistic
            // value with the server value mid-edit.
            campaignStatus: (
              existing && existing.campaignStatus !== existing.originalCampaignStatus
                ? existing.campaignStatus
                : serverStatus
            ),
            // Always refresh from server — this is the discard baseline.
            originalCampaignStatus: serverStatus,
            metaAdAccountId: c.meta_ad_account_id ?? null,
            // Mirror Google's hasPendingToggle: preserve the staged
            // toggle value but always refresh `originalOptimizationEnabled`
            // to the latest server baseline so a discard rebases correctly.
            isOptimizationEnabled: hasToggle && existing
              ? existing.isOptimizationEnabled
              : optimizationEnabled,
            originalOptimizationEnabled: optimizationEnabled,
            manualName: hasNameEdit ? (existing?.manualName ?? null) : null,
            nameValidationError: hasNameEdit
              ? (existing?.nameValidationError ?? null)
              : null,
            manualBudget: hasBudgetEdit ? (existing?.manualBudget ?? null) : null,
            // `??` not `||` — daily_budget of 0 is theoretically valid
            // and shouldn't fall back to existing.
            currentBudget: dailyBudget ?? 0,
            isCbo: Boolean(c.is_cbo),
            isDailyBudget: dailyBudget !== null,
          };
          // Reuse the prior entry object when value-equal so referential
          // equality holds for single-campaign selectors that didn't
          // change. The `changed` flag controls whether we publish a
          // new state at all — if every overlapping entry is value-equal
          // AND the size matches (no adds/removes), we skip `set()` to
          // avoid a needless subscriber cascade. The skip is structural,
          // not via a `set(() => ({}))` return — that form still bumps
          // the global state reference (default merge produces a new
          // object) and fires whole-store subscribers.
          const valueEqual =
            !!existing &&
            existing.campaignId === next.campaignId &&
            existing.currentName === next.currentName &&
            existing.campaignStatus === next.campaignStatus &&
            existing.originalCampaignStatus === next.originalCampaignStatus &&
            existing.metaAdAccountId === next.metaAdAccountId &&
            existing.isOptimizationEnabled === next.isOptimizationEnabled &&
            existing.originalOptimizationEnabled === next.originalOptimizationEnabled &&
            existing.manualName === next.manualName &&
            existing.nameValidationError === next.nameValidationError &&
            existing.manualBudget === next.manualBudget &&
            existing.currentBudget === next.currentBudget &&
            existing.isCbo === next.isCbo &&
            existing.isDailyBudget === next.isDailyBudget;
          newMap.set(c.campaign_id, valueEqual ? existing! : next);
          if (!valueEqual) changed = true;
        }
        if (changed) {
          set({ campaignSettings: newMap });
        }
      },

      clearNameChange: (campaignId) => {
        set((state) => {
          const updated = new Set(state.manualNameChanges);
          updated.delete(campaignId);
          return { manualNameChanges: updated };
        });
      },

      clearBudgetChange: (campaignId) => {
        set((state) => {
          const updated = new Set(state.manualBudgetChanges);
          updated.delete(campaignId);
          return { manualBudgetChanges: updated };
        });
      },

      discardChanges: () => {
        // Clear all dirty tracking Sets AND reset per-campaign edits
        // (manualName / manualBudget / validation errors / toggle /
        // status). Without the per-entry reset, a discard would only
        // clear the "has unsaved change?" flag while leaving the
        // typed-but-unsaved value visible to UI consumers — looks
        // saved-but-isn't from the user's perspective. Toggle resets
        // to `originalOptimizationEnabled` and status resets to
        // `originalCampaignStatus` — both are the last server
        // baselines, refreshed by `initializeCampaignSettings` /
        // `hydrateFromConfig`. Without the status reset, a user-paused
        // (then discarded) campaign stays excluded from
        // `useOptimizedCampaigns` until the next refetch.
        set((state) => {
          const cleared = new Map(state.campaignSettings);
          for (const [campaignId, settings] of cleared) {
            cleared.set(campaignId, {
              ...settings,
              manualName: null,
              nameValidationError: null,
              manualBudget: null,
              isOptimizationEnabled: settings.originalOptimizationEnabled,
              campaignStatus: settings.originalCampaignStatus,
            });
          }
          return {
            campaignSettings: cleared,
            manualNameChanges: new Set(),
            manualBudgetChanges: new Set(),
            optimizationToggleChanges: new Set(),
          };
        });
      },

      toConfigSlice: (existing) => {
        const { campaignSettings, manualNameChanges, manualBudgetChanges } = get();
        const existingList = existing?.campaign_list ?? [];

        // Only validated, in-flight names should land in the saved blob —
        // mirrors the safeManualName guard on the Google side.
        const safeManualName = (campaignId: string, settings: MetaCampaignSettings): string | null =>
          manualNameChanges.has(campaignId) && settings.nameValidationError === null
            ? settings.manualName
            : null;

        // Same guard for budget: only flush a staged budget into the
        // saved blob when the campaign is in the dirty Set. Mirrors
        // Google's settings.manualBudget ?? settings.currentBudget
        // coalescing in campaignSettingsGoogleAdsStore.ts toConfigSlice.
        const safeManualBudget = (campaignId: string, settings: MetaCampaignSettings): number | null =>
          manualBudgetChanges.has(campaignId) && settings.manualBudget !== null
            ? settings.manualBudget
            : null;

        const metaEntries = existingList
          .filter((c) => c.platform_source === 'Meta Ads')
          .map((entry) => {
            const id = String(entry.campaign_id);
            const settings = campaignSettings.get(id);
            if (!settings) return entry;
            return {
              ...entry,
              campaign_name: safeManualName(id, settings) || settings.currentName || entry.campaign_name,
              // `meaningfulStatus` strips enum prefix + treats sentinels
              // ('UNKNOWN' / 'UNSPECIFIED') as missing so a sentinel from
              // an in-flight refetch can't overwrite a meaningful prior
              // entry.campaign_status. Matches the Google slice builder's
              // pattern (campaignSettingsGoogleAdsStore.ts toConfigSlice).
              campaign_status: meaningfulStatus(settings.campaignStatus) || entry.campaign_status,
              flouri_optimize_budget: settings.isOptimizationEnabled,
              // Phase 1b — write the staged budget into the saved
              // blob so a reload after Save shows the new value, and
              // so the Cloud Run optimizer reads the user's intent
              // when there's no fresh API value yet. `budget` here is
              // the Meta entry's daily_budget; `_apply_budget` re-
              // resolves it from the saved config for idempotency
              // checks on the next batch run. Falls through to
              // currentBudget (last server-seen daily_budget) when
              // there's no staged edit, and finally to entry.budget
              // (saved-config snapshot) when neither is meaningful.
              budget: safeManualBudget(id, settings) ?? settings.currentBudget ?? entry.budget,
              // ALSO write `daily_budget` for daily-budget campaigns —
              // `mergeMetaCampaignWithSavedEntry` (the Account-page
              // overlay) reads `configCampaign.daily_budget`, not
              // `budget`. Without this, a Save+reload would keep
              // displaying the old `daily_budget` (or the API-side
              // stale value) even though `budget` was correctly
              // updated. Lifetime campaigns keep their existing
              // `entry.daily_budget` (typically null) — `manualBudget`
              // is gated null for them by the BudgetBar UI.
              daily_budget: settings.isDailyBudget
                ? (safeManualBudget(id, settings) ?? settings.currentBudget ?? entry.daily_budget)
                : entry.daily_budget,
            };
          });

        return { campaign_list: metaEntries };
      },

      hydrateFromConfig: (config) => {
        if (!config?.campaign_list || !Array.isArray(config.campaign_list)) return;
        // Compute outside `set` so the no-op branch can skip publishing
        // entirely — `set(() => ({}))` is NOT a no-op (merge produces
        // a new state ref). Same posture as initializeCampaignSettings.
        const state = get();
        const updated = new Map(state.campaignSettings);
        let touched = false;
        for (const saved of config.campaign_list) {
          if (saved.platform_source !== 'Meta Ads') continue;
          const id = String(saved.campaign_id);
          const existing = updated.get(id);
          if (!existing) continue;
          const optimizationEnabled = saved.flouri_optimize_budget === true;
          const savedStatus = meaningfulStatus(saved.campaign_status) || existing.campaignStatus;
          updated.set(id, {
            ...existing,
            currentName: saved.campaign_name || existing.currentName,
            // Strip enum prefix + skip 'UNKNOWN' / 'UNSPECIFIED' sentinels
            // so a sentinel from saved config can't push a non-meaningful
            // value into store state. Matches savedConfigMerge's overlay
            // behavior + Google store's hydrate (both via meaningfulStatus).
            campaignStatus: savedStatus,
            // Saved-config status is the discard baseline going forward
            // (it's the most recent server-confirmed value). Without
            // this refresh, a pause-then-save-then-discard cycle would
            // revert to the pre-save baseline.
            originalCampaignStatus: savedStatus,
            // `meta_ad_account_id` is typed `string | null` — null is a
            // meaningful value (campaign reassigned away from an ad
            // account), not a "missing" sentinel. `??` would coalesce
            // saved-null to existing, preserving stale data. Use a
            // distinct undefined-check so an explicit null round-trips
            // while a truly-missing key (legacy config predating the
            // field) still falls back to existing.
            metaAdAccountId: saved.meta_ad_account_id !== undefined
              ? saved.meta_ad_account_id
              : existing.metaAdAccountId,
            isOptimizationEnabled: optimizationEnabled,
            originalOptimizationEnabled: optimizationEnabled,
            // `??` (not `||`) for budget — 0 is valid (saved snapshot
            // of a paused/zero-budget campaign). `||` would treat 0
            // as missing and silently preserve the stale prior value.
            currentBudget: saved.budget ?? existing.currentBudget,
            // Clear in-flight edits — saved state is now authoritative.
            // Same pattern as Google's hydrateFromConfig.
            manualName: null,
            nameValidationError: null,
            manualBudget: null,
          });
          touched = true;
        }
        if (touched) {
          set({ campaignSettings: updated });
        }
        debug.log('MetaCampaignSettings: hydrated campaign_list from saved config');
      },

      markSaved: () => {
        // Match Google's markSaved: clear the dirty tracking Sets only.
        // Per-entry manualName / nameValidationError / manualBudget cleanup
        // happens on the next hydrateFromConfig (driven by
        // appConfigService.loadAppConfig on the next page mount or explicit
        // reload).
        set({
          manualNameChanges: new Set(),
          manualBudgetChanges: new Set(),
          optimizationToggleChanges: new Set(),
        });
      },
    }),
    { name: 'campaign-settings-meta-ads-store' },
  ),
);

